import { useState } from 'react';
import { Link2 } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { AppCard } from '../ui/AppCard';
import { Button } from '../ui/Button';
import { TextField } from '../ui/TextField';
import type { Mapping } from '../../types';

// Edits mapping.spec.target in place - the endpoint/file an Agent source
// polls or reads, or the URL a Scraper connector (internal/scrapequeue)
// polls on a schedule. Previously only reachable through the raw-JSON
// Advanced editor (MappingManageScreen); re-pointing a source at a new URL
// (a host moved, a path changed) is common enough on its own to deserve a
// dedicated field, same reasoning as CredentialsCard getting its own.
// Renders nothing for a mapping whose spec carries no `target` at all
// (e.g. a webhook).
export function TargetUrlCard({ mapping }: { mapping: Mapping }) {
  const { updateMapping } = useStore();
  const target = typeof mapping.spec?.target === 'string' ? mapping.spec.target : undefined;
  const isFile = target?.startsWith('file://') ?? false;
  const label = isFile ? 'File path' : 'URL';

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(target ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (target === undefined) return null;

  const save = async () => {
    const next = value.trim();
    if (!next) {
      setError(`Enter a ${label.toLowerCase()}.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateMapping({ ...mapping, spec: { ...mapping.spec, target: next } });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setEditing(false);
    setValue(target);
    setError('');
  };

  return (
    <AppCard className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <Link2 size={15} className="text-black/50 dark:text-white/50" />
        <span className="text-sm font-semibold">{label}</span>
      </div>
      {editing ? (
        <>
          <TextField aria-label={label} value={value} onChange={(e) => setValue(e.target.value)} spellCheck={false} />
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="bordered" onClick={cancel} disabled={saving}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono">{target || '(not set)'}</code>
          <Button variant="bordered" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </div>
      )}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </AppCard>
  );
}
